/**
 * User Update Operation
 * Isolated feature: Handle user profile updates
 */
import { useState, useCallback } from 'react';
import { userService } from '../service';
import { logger } from '@/shared/utils';
import type { UpdateUserRequest, User } from '@/shared/types';

export const useUpdateUser = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateUser = useCallback(async (userData: UpdateUserRequest): Promise<User | null> => {
    setLoading(true);
    setError(null);

    try {
      logger.debug('Starting user update', 'useUpdateUser', { userData });
      const updatedUser = await userService.updateUser(userData);
      logger.info('User update successful', 'useUpdateUser', { userId: updatedUser?.id });
      return updatedUser;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Profile update failed';
      setError(errorMessage);
      logger.error('User update error', 'useUpdateUser', { error: err });
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    updateUser,
    loading,
    error,
    clearError
  };
};
